import { AsyncSubject, type Observable } from 'rxjs';
import { Auth } from '@aws-amplify/auth';
import { createAction } from '@reduxjs/toolkit';
import userService from './userService';
import type { AppDispatch } from '../data/store';
import * as uiSlice from '../data/ui.slice';

interface Environment {
  REACT_APP_AWS_DOMAIN: string;
  REACT_APP_AWS_USER_POOL_REGION: string;
  REACT_APP_AWS_USER_POOL_ID: string;
  REACT_APP_AWS_CLIENT_ID: string;
  REACT_APP_AWS_SIGNIN_URI: string;
  REACT_APP_AWS_SIGNOUT_URI: string;
  REACT_APP_AWS_CUSTOM_PROVIDER: string;
}

const { __environment__ } = window as unknown as {
  __environment__: Environment;
};

const region = __environment__.REACT_APP_AWS_USER_POOL_REGION;
const userPool = __environment__.REACT_APP_AWS_USER_POOL_ID;
const clientId = __environment__.REACT_APP_AWS_CLIENT_ID;
const domain = __environment__.REACT_APP_AWS_DOMAIN;
const signinUri = __environment__.REACT_APP_AWS_SIGNIN_URI;
const signoutUri = __environment__.REACT_APP_AWS_SIGNOUT_URI;
const customProvider = __environment__.REACT_APP_AWS_CUSTOM_PROVIDER;
// const callbackUri = __environment__.REACT_APP_AWS_LOGIN_CALLBACK_URI;
// const hostedSigninUri = __environment__.REACT_APP_AWS_HOSTED_SIGNIN_URI;
// const signoutUri = __environment__.REACT_APP_AWS_SIGNOUT_URI;
// const tokenScopes = ['openid', 'email', 'profile'];

export interface Tokens {
  accessToken?: string;
  idToken?: string;
}

export interface MagicLinkAuthResult {
  ChallengeParameters?: Record<string, string>;
  AuthenticationResult?: {
    AccessToken?: string;
    ExpiresIn?: number;
    TokenType?: string;
    IdToken?: string;
    RefreshToken?: string;
  };
}

const resetStore = createAction('RESET_STORE');

class AuthService {
  private loggedIn = false;
  private newPasswordInfo: any = null;
  private tier3Tokens: Tokens = {
    accessToken: '',
    idToken: '',
  };
  private dispatch: AppDispatch | null = null;

  constructor() {
    Auth.configure({
      region: region,
      userPoolId: userPool,
      userPoolWebClientId: clientId,
      oauth: {
        domain: domain,
        scope: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
        redirectSignIn: signinUri,
        redirectSignOut: signoutUri,
        responseType: 'code',
      },
    });

    Auth.currentSession()
      .then((session) => {
        this.loggedIn = session.isValid();
      })
      .catch(() => {
        this.loggedIn = false;
      });
  }

  public setDispatch(dispatch: AppDispatch) {
    this.dispatch = dispatch;
  }

  public getUserInfo(): Observable<{
    name: string;
    email: string;
  }> {
    const subject = new AsyncSubject<{
      name: string;
      email: string;
    }>();
    Auth.currentAuthenticatedUser().then((userInfo) => {
      subject.next({
        name: userInfo.attributes.email,
        email: userInfo.attributes.email,
      });
      subject.complete();
    });
    return subject;
  }

  public isLoggedIn(): boolean {
    return this.loggedIn;
  }

  public getTokens(): Observable<Tokens> {
    const subject = new AsyncSubject<Tokens>();
    Auth.currentSession()
      .then((session) => {
        subject.next({
          accessToken: session?.getAccessToken()?.getJwtToken(),
          idToken: session?.getIdToken()?.getJwtToken(),
        });
        subject.complete();
      })
      .catch((err) => {
        subject.next({});
        subject.complete();
      });
    return subject;
  }

  public getTier3Tokens(): Observable<Tokens> {
    const subject = new AsyncSubject<Tokens>();
    if (this.tier3Tokens.accessToken && this.tier3Tokens.idToken) {
      subject.next(this.tier3Tokens);
      subject.complete();
    } else {
      // If tokens are empty, we'll wait for them to be set
      const checkTokens = () => {
        if (this.tier3Tokens.accessToken && this.tier3Tokens.idToken) {
          subject.next(this.tier3Tokens);
          subject.complete();
        } else {
          setTimeout(checkTokens, 100); // Check again after 100ms
        }
      };
      checkTokens();
    }
    return subject;
  }

  public async handleLoginCallback(url: string): Promise<void> {
    return new Promise(async (resolve) => {
      Auth.currentSession()
        .then((session) => {
          this.loggedIn = session.isValid();
        })
        .finally(() => resolve());
    });
  }

  public login(
    username: string,
    password: string,
    isProvider: boolean = true,
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      Auth.configure({
        authenticationFlowType: isProvider
          ? 'USER_PASSWORD_AUTH'
          : 'USER_SRP_AUTH',
      });

      Auth.signIn(username, password)
        .then((value) => {
          if (value.challengeName === 'NEW_PASSWORD_REQUIRED') {
            this.newPasswordInfo = value;
            resolve(false);
          } else {
            this.loggedIn = true;
            resolve(true);
          }
        })
        .catch((err) => {
          reject(err?.message || 'Login failed');
        });
    });
  }

  public loginMagicLink(magicLinkId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      userService.magicLink(magicLinkId).subscribe({
        next: (token: any) => {
          if (token?.AuthenticationResult) {
            const accessToken = token?.AuthenticationResult?.AccessToken;
            const refreshToken = token?.AuthenticationResult?.RefreshToken;
            const idToken = token?.AuthenticationResult?.IdToken;
            if (!accessToken || !idToken) {
              return;
            }
            this.tier3Tokens = {
              accessToken,
              idToken,
            };
          }
          resolve();
        },
        error: (err) => {
          reject(err);
          if (this.dispatch) {
            this.dispatch(uiSlice.setErrorMessage(err.message));
          }
        },
      });
    });
  }

  public loginFederated(): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.federatedSignIn({
        customProvider,
      })
        .then((value) => {
          this.loggedIn = true;
          resolve();
        })
        .catch((err) => {
          reject('Loggin failed');
        });
    });
  }

  public logout(): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.signOut()
        .then(() => {
          this.loggedIn = false;
          this.dispatch!(resetStore());
          resolve();
        })
        .catch((err) => {
          reject('Logout failed');
        });
    });
  }

  public globalSignout(): Promise<void> {
    return new Promise((resolve, reject) => {
      userService.globalSignout().subscribe({
        next: () => resolve(),
        error: (err) => reject(err),
      });
    });
  }

  public forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.forgotPassword(email)
        .then((evt) => {
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public sendPasswordResetRequest(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.forgotPassword(email)
        .then(() => {
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public resetPassword(
    email: string,
    code: string,
    password: string,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.forgotPasswordSubmit(email, code, password)
        .then(() => {
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  public activateAccount(password: string): Promise<void> {
    return new Promise((resolve, reject) => {
      Auth.completeNewPassword(this.newPasswordInfo, password)
        .then(() => {
          this.loggedIn = true;
          resolve();
        })
        .catch((err) => {
          reject(err);
        })
        .finally(() => {
          this.newPasswordInfo = null;
        });
    });
  }
}

const authService = new AuthService();

export default authService;
